import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScoreGauge } from "@/components/ScoreGauge";
import { tierFor, formatCount, formatDate } from "@/lib/utils-app";
import {
  Eye, ThumbsUp, MessageCircle, Clock, Calendar, User,
  Trophy, AlertTriangle, Lightbulb, Layers,
} from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border bg-secondary/40 px-3 py-2.5">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <div className="tabular text-sm font-semibold truncate">{value}</div>
        <div className="overline text-[0.6rem] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

const BREAKDOWN_SECTIONS = [
  { key: "hook", label: "Hook" },
  { key: "creative_structure", label: "Creative Structure" },
  { key: "visual_strategy", label: "Visual Strategy" },
  { key: "copywriting", label: "Copywriting" },
  { key: "cta", label: "Call-to-Action" },
  { key: "audience", label: "Audience" },
  { key: "pacing", label: "Pacing & Rhythm" },
  { key: "audio", label: "Audio & Sound" },
  { key: "trend_context", label: "Trend Context" },
];

export function AdAnalysisDetail({ analysis }) {
  if (!analysis) return null;
  const meta = analysis.metadata || {};
  const ai = analysis.ai_analysis || {};
  const tier = tierFor(analysis.overall_score || 0);
  const isShort = meta.video_type === "short";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="analysis-detail">
      {/* Video header */}
      <Card className="overflow-hidden">
        <div className="grid lg:grid-cols-5 gap-0">
          <div className={`lg:col-span-3 bg-black ${isShort ? "flex justify-center" : ""}`}>
            <div className={isShort ? "w-full max-w-[320px] aspect-[9/16]" : "w-full aspect-video"}>
              <iframe
                title={meta.title}
                src={meta.embed_url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="youtube-player"
              />
            </div>
          </div>
          <div className="lg:col-span-2 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1 border-primary/40 text-primary">
                {isShort ? "Short" : "Video"}
              </Badge>
              <Badge style={{ backgroundColor: tier.color + "22", color: tier.color }} className="border-0">
                {tier.badge}
              </Badge>
            </div>
            <h2 className="font-display text-lg sm:text-xl font-semibold leading-tight tracking-tight" data-testid="video-title">
              {meta.title}
            </h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span data-testid="video-channel">{meta.channel}</span>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Stat icon={Eye} label="Views" value={formatCount(meta.views)} />
              <Stat icon={ThumbsUp} label="Likes" value={formatCount(meta.likes)} />
              <Stat icon={MessageCircle} label="Comments" value={formatCount(meta.comments)} />
              <Stat icon={Clock} label="Duration" value={meta.duration || "—"} />
              <Stat icon={Calendar} label="Published" value={formatDate(meta.published_at)} />
              <Stat icon={Calendar} label="Analyzed" value={formatDate(analysis.created_at)} />
            </div>
          </div>
        </div>
      </Card>

      {/* Score + categories */}
      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 p-6 flex flex-col items-center justify-center text-center">
          <p className="overline text-muted-foreground mb-4">Overall Creative Score</p>
          <ScoreGauge score={analysis.overall_score || 0} />
          <p className="mt-4 text-sm text-muted-foreground max-w-xs">{ai.summary}</p>
        </Card>

        <Card className="lg:col-span-3 p-6">
          <p className="overline text-muted-foreground mb-4">Category Breakdown · {(ai.categories || []).length} dimensions</p>
          <div className="grid sm:grid-cols-2 gap-x-5 gap-y-4">
            {(ai.categories || []).map((c, i) => {
              const t = tierFor(c.score);
              return (
                <motion.div
                  key={c.key}
                  data-testid={`category-${c.key}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.03 * i, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-xs font-medium truncate pr-2">{c.name}</span>
                    <span className="tabular text-sm font-semibold" style={{ color: t.color }}>
                      {c.score}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${c.score}%` }}
                      transition={{ delay: 0.05 * i + 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                      style={{ backgroundColor: t.color, boxShadow: `0 0 12px ${t.color}55` }}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{c.explanation}</p>
                </motion.div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* AI Breakdown */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="h-4 w-4 text-primary" />
          <h3 className="font-display text-lg font-semibold tracking-tight">AI Breakdown</h3>
        </div>
        <Tabs defaultValue="breakdown">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="breakdown" data-testid="tab-breakdown">Deep Dive</TabsTrigger>
            <TabsTrigger value="winning" data-testid="tab-winning">Winning Elements</TabsTrigger>
            <TabsTrigger value="weaknesses" data-testid="tab-weaknesses">Weaknesses</TabsTrigger>
            <TabsTrigger value="recommendations" data-testid="tab-recommendations">Recommendations</TabsTrigger>
          </TabsList>

          <TabsContent value="breakdown" className="mt-5">
            <div className="grid sm:grid-cols-2 gap-4">
              {BREAKDOWN_SECTIONS.map((s) => (
                <div key={s.key} className="rounded-xl border border-border p-4">
                  <p className="overline text-muted-foreground mb-1.5">{s.label}</p>
                  <p className="text-sm leading-relaxed">{ai.breakdown?.[s.key] || "—"}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="winning" className="mt-5 space-y-2.5">
            {(ai.winning_elements || []).map((w, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <Trophy className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
                <span>{w}</span>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="weaknesses" className="mt-5 space-y-2.5">
            {(ai.weaknesses || []).map((w, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                <span>{w}</span>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="recommendations" className="mt-5 space-y-2.5">
            {(ai.recommendations || []).map((w, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <Lightbulb className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <span>{w}</span>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
